import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { useI18n } from "../../i18n";
import { createInvite, listMembers, removeMember } from "../../api/collab";
import type { WorkspaceMember } from "../../types/collab";
import { Avatar } from "./avatar";

/**
 * Members view: member list (column 2) + selected member's card or
 * the invite panel (column 3). Owner can invite + remove members.
 */
export function MembersView({ accountId, workspaceName }: { accountId: string; workspaceName: string }) {
  const { user, client } = useAuth();
  const { t } = useI18n();
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOwner = members.find((m) => m.userId === user?.id)?.role === "owner";

  const refresh = useCallback(async () => {
    if (!accountId || !client) return;
    try {
      const res = await listMembers(client, accountId);
      setMembers(res.members);
      setError(null);
    } catch {
      setError(t("collab.members.error"));
    }
  }, [accountId, client, t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const invite = useCallback(async () => {
    if (!client) return;
    setBusy(true);
    setError(null);
    try {
      const { token } = await createInvite(client, accountId);
      setInviteUrl(`${window.location.origin}/?invite=${token}`);
      setCopied(false);
    } catch {
      setError(t("collab.members.error"));
    } finally {
      setBusy(false);
    }
  }, [accountId, client, t]);

  const remove = useCallback(
    async (userId: string) => {
      if (!client) return;
      try {
        await removeMember(client, accountId, userId);
        if (selectedId === userId) setSelectedId(null);
        await refresh();
      } catch {
        setError(t("collab.members.removeFailed"));
      }
    },
    [accountId, client, refresh, selectedId, t]
  );

  const copy = useCallback(() => {
    if (!inviteUrl) return;
    void navigator.clipboard?.writeText(inviteUrl).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }, [inviteUrl]);

  const openInvite = () => {
    setInviting(true);
    if (!inviteUrl) void invite();
  };

  const selected = members.find((m) => m.userId === selectedId) ?? null;
  const nameOf = (m: WorkspaceMember) => m.username ? `@${m.username}` : m.name || m.email || m.userId;

  const detail = inviting || !!selected;
  const back = () => { setInviting(false); setSelectedId(null); };

  return (
    <div className={`ws-view${detail ? " detail" : ""}`}>
      <aside className="ws-side">
        <div className="ws-side-head"><span className="ws-side-headtitle">{workspaceName}</span></div>
        <div className="ws-side-scroll">
          <div className="ws-side-section">
            <div className="ws-side-section-head">
              <span className="ws-side-label">{t("collab.members")}</span>
              <button type="button" className="ws-icon-btn" onClick={openInvite} title={t("collab.members.invite")} aria-label={t("collab.members.invite")}>
                +
              </button>
            </div>
            <ul className="ws-member-list">
            {members.map((m) => (
              <li key={m.userId}>
                <button
                  type="button"
                  className={`ws-member-item${m.userId === selectedId && !inviting ? " active" : ""}`}
                  onClick={() => { setSelectedId(m.userId); setInviting(false); }}
                >
                  <Avatar name={nameOf(m)} id={m.userId} size={28} />
                  <span className="ws-member-item-name">{m.userId === user?.id ? `${nameOf(m)} (${t("collab.channels.you")})` : nameOf(m)}</span>
                  <span className={`ws-role ${m.role}`}>{t(`collab.role.${m.role}`)}</span>
                </button>
              </li>
            ))}
            </ul>
          </div>
        </div>
      </aside>

      <section className="ws-content">
        {detail ? <button type="button" className="ws-mback" onClick={back}>‹ {t("collab.members")}</button> : null}
        <div className="ws-detail-scroll">
          {inviting ? (
            <div className="ws-detail">
              <div className="ws-detail-label">{t("collab.members.invite")}</div>
              {inviteUrl ? (
                <div className="ws-invite">
                  <pre className="ws-cmd"><code>{inviteUrl}</code></pre>
                  <div className="ws-connect-actions">
                    <button type="button" className="ws-btn" onClick={copy}>
                      {copied ? t("collab.connect.copied") : t("collab.connect.copy")}
                    </button>
                    <button type="button" className="ws-btn ghost" onClick={() => void invite()} disabled={busy}>
                      {t("collab.members.inviteNew")}
                    </button>
                  </div>
                  <p className="ws-hint">{t("collab.members.inviteHint")}</p>
                </div>
              ) : (
                <p className="ws-muted">…</p>
              )}
            </div>
          ) : selected ? (
            <div className="ws-detail">
              <div className="ws-detail-hero">
                <Avatar name={nameOf(selected)} id={selected.userId} size={56} />
                <div>
                  <h3 className="ws-detail-name">{nameOf(selected)}</h3>
                  <p className="ws-detail-status">
                    <span className={`ws-role ${selected.role}`}>{t(`collab.role.${selected.role}`)}</span>
                  </p>
                </div>
              </div>
              {selected.email ? (
                <div className="ws-detail-section">
                  <dl className="ws-detail-grid">
                    <dt>{t("collab.members.email")}</dt>
                    <dd>{selected.email}</dd>
                  </dl>
                </div>
              ) : null}
              {isOwner && selected.role !== "owner" ? (
                <div className="ws-detail-actions">
                  <button type="button" className="ws-btn danger" onClick={() => void remove(selected.userId)}>
                    {t("collab.members.remove")}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="ws-content-empty">{t("collab.members.selectPrompt")}</div>
          )}
          {error ? <p className="ws-warn">{error}</p> : null}
        </div>
      </section>
    </div>
  );
}
